import React from 'react';
import styled from '@emotion/styled';
import {
	BaseLink,
	Css,
	FacebookIcon,
	H2,
	InstagramIcon,
	LinkedInIcon,
	XLogoIcon,
	YouTubeIcon,
} from '../../../../../ui';
import { IYoutubeCollection } from '../../../domain/models/IYoutubeCollection';
import { YoutubeCollection } from './YoutubeCollection';
import { Color } from '../../../../shared/styles/Color';
import { useTranslation } from '../../../../shared/i18n/translate/useTranslation';
import { ISocialMediaConfig } from '../../../domain/models/ISocialMediaConfig';
import { FacebookSection } from './FacebookSection';
import Responsive from '../../../../../ui/utils/Responsive';
import ContentWrap from '../../../../../ui/components/Layout/ContentWrap';
import { SocialCard } from './SocialCard';
import { InstagramSection } from './InstagramSection';
import { YoutubeSubscribe } from './YoutubeSubscribe';

export interface ISocialMediaSectionProps {
	youtubeVideoCollection: IYoutubeCollection | null;
	socialMediaConfig: ISocialMediaConfig;
}

export const SocialMediaSection = ({
	youtubeVideoCollection,
	socialMediaConfig,
}: ISocialMediaSectionProps) => {
	const { t } = useTranslation();

	return (
		<SectionWrapper>
			<StyledContentWrap>
				<SectionTitle style={{ paddingLeft: 16 }}>
					{t('social_media_title')}
				</SectionTitle>
				<StyledEmbeddedSocialMedia>
					<StyledFirstColumn>
						<SocialCard title={t('social_media_facebook_title')}>
							<FacebookSection
								frameTitle={t('social_media_facebook_frame_title')}
								facebookPageId={socialMediaConfig.facebookPageId}
							/>
						</SocialCard>
					</StyledFirstColumn>
					<StyledSecondColumn>
						<SocialCard
							title={t('social_media_youtube_title')}
							endContent={
								<YoutubeSubscribe
									channel={socialMediaConfig.youtubeChannel}
									frameTitle={t('social_media_youtube_frame_title')}
								/>
							}
						>
							<YoutubeCollection
								youtubeVideoCollection={youtubeVideoCollection}
							/>
						</SocialCard>
					</StyledSecondColumn>
				</StyledEmbeddedSocialMedia>

				<SocialCard title={t('social_media_instagram_title')}>
					<InstagramSection />
				</SocialCard>

				<StyledExternalLinks>
					<StyledLink
						target="_blank"
						rel="noreferrer"
						href={`https://facebook.com/${socialMediaConfig.facebookPageId}`}
					>
						<StyledFacebookIcon />
						{t('social_media_visit_facebook_button')}
					</StyledLink>
					<StyledLink
						target="_blank"
						rel="noreferrer"
						href={socialMediaConfig.instagramLink}
					>
						<StyledInstagramIcon />
						{t('social_media_visit_instagram_button')}
					</StyledLink>
					<StyledLink
						target="_blank"
						rel="noreferrer"
						href={socialMediaConfig.twitterLink}
					>
						<StyledXIcon />
						{t('social_media_visit_X_button')}
					</StyledLink>
					<StyledLink
						target="_blank"
						rel="noreferrer"
						href={socialMediaConfig.linkedinLink}
					>
						<StyledLinkedInIcon />
						{t('social_media_visit_linkedin_button')}
					</StyledLink>
					<StyledLink
						target="_blank"
						rel="noreferrer"
						href={`https://youtube.com/${socialMediaConfig.youtubeChannel}`}
					>
						<StyledYouTubeIcon />
						{t('social_media_visit_youtube_button')}
					</StyledLink>
				</StyledExternalLinks>
			</StyledContentWrap>
		</SectionWrapper>
	);
};

const StyledContentWrap = styled(ContentWrap)`
	@media (${Responsive.getMediaQueryForBreakpoint('mobile')}) {
		margin: 0;
	}
`;

const StyledLink = styled(BaseLink)`
	line-height: 24px;
	font-size: ${Css.pxToRem(16)};
`;

const StyledExternalLinks = styled.div`
	display: flex;
	gap: 32px;
	margin-top: 32px;
	border-top: 1px solid ${Color.Neutral70};
	padding-top: 24px;

	@media (${Responsive.getMediaQueryForBreakpoint('tablet')}) {
		flex-direction: column;
		gap: 8px;
	}

	@media (${Responsive.getMediaQueryForBreakpoint('mobile')}) {
		margin-left: ${Css.pxToRem(16)};
		margin-right: ${Css.pxToRem(16)};
	}
`;

const StyledEmbeddedSocialMedia = styled.div`
	display: flex;
	flex-direction: row;
	height: 100%;
	justify-content: stretch;
	align-items: stretch;
	margin-top: ${Css.pxToRem(32)};
	gap: 116px;
	padding-bottom: ${Css.pxToRem(56)};

	@media (${Responsive.getMediaQueryForBreakpoint('tabletXL')}) {
		margin-top: ${Css.pxToRem(16)};
	}

	@media (${Responsive.getMediaQueryForBreakpoint('tablet')}) {
		gap: 24px;
	}

	@media (${Responsive.getMediaQueryForBreakpoint('mobileXL')}) {
		flex-direction: column;
		padding-bottom: ${Css.pxToRem(40)};
		gap: ${Css.pxToRem(40)};
	}
`;

const StyledFirstColumn = styled.div`
	flex: 0 0 auto;
	height: 100%;
	display: flex;
	justify-content: start;
	align-items: start;

	@media (${Responsive.getMediaQueryForBreakpoint('mobileXL')}) {
		width: 100%;
	}
`;

const StyledSecondColumn = styled.div`
	flex: 1;
	height: 100%;
	display: flex;
	justify-content: start;
	align-items: start;
`;

const SectionTitle = styled(H2)`
	margin: 0;
	flex: 1;

	@media (${Responsive.getMediaQueryForBreakpoint('mobileXL')}) {
		font-size: ${Css.pxToRem(26)};
	}
`;

const SectionWrapper = styled.div`
	flex-grow: 1;
	padding: ${Css.pxToRem(80)} 0;
	background-color: ${Color.Surface};

	@media (${Responsive.getMediaQueryForBreakpoint('mobileXL')}) {
		padding: ${Css.pxToRem(40)} 0;
	}
`;

const withIconStyles = (
	Icon: React.ComponentType<React.SVGProps<HTMLOrSVGImageElement>>,
) => styled(Icon)`
	width: ${Css.pxToRem(24)};
	height: ${Css.pxToRem(24)};
	margin-right: ${Css.pxToRem(8)};
	margin-top: ${Css.pxToRem(-3)};
	g {
		fill: ${Color.PrimaryDefault};
	}
`;

const StyledFacebookIcon = withIconStyles(FacebookIcon);
const StyledXIcon = withIconStyles(XLogoIcon);
const StyledYouTubeIcon = withIconStyles(YouTubeIcon);
const StyledInstagramIcon = withIconStyles(InstagramIcon);
const StyledLinkedInIcon = withIconStyles(LinkedInIcon);
